import { useEffect, useState } from 'react';

import { CircularProgress, Sheet, Typography } from '@mui/joy';
import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '../../services/react-query/queryKeys';
import { useGlobalStore } from '../../stores/useGlobalStore';

import { getDemoSession } from './api';
import { isDemoZone } from './constants';
import useResolveDemoLink from './hooks/useResolveDemoLink';

const readToken = (): string => (new URLSearchParams(window.location.search).get('t') ?? '').trim();

const FullScreen = ({ children }: { children: React.ReactNode }) => (
  <Sheet sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    {children}
  </Sheet>
);

// In the demo zone, resolve the magic-link session (only demo APIs) and set the
// demo cookie before rendering the app, so feature calls authenticate. Outside
// the demo zone this is inert — it never calls a demo API.
//   - /demo?t=… → POST /auth/demo installs the (new) cookie + session.
//   - bare /demo (reload) → GET /user/demo/me rehydrates from the cookie.
export const DemoGate = ({ children }: { children: React.ReactNode }) => {
  const demoSession = useGlobalStore((s) => s.demoSession);
  const setDemoSession = useGlobalStore((s) => s.setDemoSession);
  const [token] = useState(() => (isDemoZone ? readToken() : ''));

  useEffect(() => {
    if (token) {
      // New link: drop any previously stored session so the app never renders
      // on the old demo's data, and scrub the token from the URL.
      setDemoSession(null);
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [token, setDemoSession]);

  const resolve = useResolveDemoLink(token);
  // On a bare /demo (reload / new tab), always validate the cookie via
  // /user/demo/me — even if a session is cached in sessionStorage.
  const me = useQuery({
    queryKey: queryKeys.demo.me(),
    queryFn: ({ signal }) => getDemoSession(signal),
    enabled: isDemoZone && !token,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const resolved = resolve.data ?? me.data ?? null;
  useEffect(() => {
    if (resolved) setDemoSession(resolved);
  }, [resolved, setDemoSession]);

  // /user/demo/me says the cookie is no longer valid → drop any stale session.
  useEffect(() => {
    if (!token && me.isFetched && !me.data) setDemoSession(null);
  }, [token, me.isFetched, me.data, setDemoSession]);

  if (!isDemoZone) return <>{children}</>;

  const invalidMessage = (
    <FullScreen>
      <Typography level='body-md' color='neutral'>
        This demo link is invalid or has expired. Please open your demo link again.
      </Typography>
    </FullScreen>
  );
  const loader = (
    <FullScreen>
      <CircularProgress size='lg' />
    </FullScreen>
  );

  // A fresh link (?t=) always re-resolves and installs the NEW cookie/session,
  // overriding any previously stored one — block until it completes so the app
  // never runs on the previous demo's cookie.
  if (token) {
    if (resolve.isError) return invalidMessage;
    if (!resolve.data) return loader;
    return <>{children}</>;
  }

  // Bare /demo (reload): rehydrate from the cookie via GET /user/demo/me.
  if (!demoSession) {
    if (me.isError || (me.isFetched && !me.data)) return invalidMessage;
    return loader;
  }

  return <>{children}</>;
};
